/** The `auth` area: sign-in, sign-up, password reset and email verification.
 *  Owned by the stage 2 slot components-a (docs/I18N.md §11). */
export const auth = {
  // The sign-in / sign-up form.
  'auth.form.titleSignUp': 'Create your account',
  'auth.form.titleSignIn': 'Sign in',
  'auth.form.subtitleInviteOnly':
    'Tesserano is invite-only. Use the address your invite was sent to.',
  'auth.form.subtitleSignUp': 'Join your group on Tesserano.',
  'auth.form.subtitleSignIn': 'Welcome back to Tesserano.',
  'auth.form.createWith': 'Create account with {provider}',
  'auth.form.name': 'Name',
  'auth.form.namePlaceholder': 'Your name',
  'auth.form.email': 'Email',
  'auth.form.emailPlaceholder': 'you@example.com',
  'auth.form.code': 'Code',
  'auth.form.password': 'Password',
  'auth.form.passwordNewPlaceholder': 'At least 8 characters',
  'auth.form.passwordPlaceholder': 'Your password',
  'auth.form.resendCode': 'Resend code',
  'auth.form.sendCode': 'Send code',
  'auth.form.usePassword': 'Use password instead',
  'auth.form.forgotPassword': 'Forgot password?',
  'auth.form.useCode': 'Email me a code instead',
  'auth.form.wait': 'Please wait…',
  'auth.form.createAccount': 'Create account',
  'auth.form.signInWithCode': 'Sign in with code',
  'auth.form.signIn': 'Sign in',
  'auth.form.or': 'or',
  'auth.form.continueWith': 'Continue with {provider}',
  'auth.form.haveAccount': 'Already have an account? {link}',
  'auth.form.signInLink': 'Sign in',
  'auth.form.newHere': 'New here? {link}',
  'auth.form.createAccountLink': 'Create an account',
  /** The only answer a code request gives, whatever the address. */
  'auth.form.codeRequested': 'If that address has an account, a code is on its way.',
  // The form's own failures.
  'auth.error.createAccount': 'Could not create the account.',
  'auth.error.code': 'That code did not work. Request a new one.',
  'auth.error.signIn': 'Could not sign in.',
  'auth.error.generic': 'Something went wrong. Please try again.',
  // A refused provider round trip (`?error=`).
  'auth.provider.notLinked':
    'An account already uses that address. Sign in with your password, then connect this provider under Account.',
  'auth.provider.accessDenied':
    'Your provider refused that sign-in. Your account there may not have access to Tesserano.',
  'auth.provider.signupClosed': 'That provider cannot create an account here without an invite.',
  'auth.provider.noAccountFor': 'No Tesserano account uses that {provider} identity yet.',
  'auth.provider.noAccount':
    'No account here uses that identity yet. If you were invited, create your account from Create an account, with the same provider.',
  'auth.provider.failed':
    'That sign-in did not go through. Try again, or use your email and password.',
  // Password reset.
  'auth.reset.title': 'Reset your password',
  'auth.reset.subtitleRequest': 'We’ll send a code to your email.',
  'auth.reset.subtitleReset': 'Enter the code and choose a new password.',
  'auth.reset.sendCode': 'Send reset code',
  'auth.reset.code': 'Reset code',
  'auth.reset.newPassword': 'New password',
  'auth.reset.submit': 'Reset password',
  'auth.reset.sent': 'Check your email for a reset code (printed to the server console in dev).',
  'auth.reset.done': 'Password reset. Redirecting to sign in…',
  'auth.reset.sendFailed': 'Could not send a reset code.',
  'auth.reset.noCredential':
    'This account has no password to reset. Sign in with your provider, then set a password under Account.',
  'auth.backToSignIn': 'Back to sign in',
  // Email verification.
  'auth.verify.title': 'Verify your email',
  'auth.verify.subtitle':
    'Enter the 6-digit code we sent you (printed to the server console in dev).',
  'auth.verify.code': 'Verification code',
  'auth.verify.submit': 'Verify email',
  'auth.verify.didntGetIt': 'Didn’t get it? {resend}',
  'auth.verify.resend': 'Resend code',
  'auth.verify.emailFirst': 'Enter your email first.',
  'auth.verify.resendFailed': 'Could not send a new code.',
  'auth.verify.resent': 'A new code is on its way (printed to the server console in dev).',
  /** better-auth's refusals by code (src/lib/auth-errors.ts): the English is
   *  the library's own text. */
  'auth.refusal.invalidEmailOrPassword': 'Invalid email or password',
  'auth.refusal.invalidPassword': 'Invalid password',
  'auth.refusal.invalidEmail': 'Invalid email',
  'auth.refusal.userExists': 'User already exists.',
  'auth.refusal.userExistsUseAnother': 'User already exists. Use another email.',
  'auth.refusal.passwordTooShort': 'Password too short',
  'auth.refusal.passwordTooLong': 'Password too long',
  'auth.refusal.invalidCode': 'Invalid OTP',
  'auth.refusal.codeExpired': 'OTP expired',
  'auth.refusal.tooManyAttempts': 'Too many attempts',
  'auth.refusal.invalidToken': 'Invalid token',
  'auth.refusal.tokenExpired': 'Token expired',
  'auth.refusal.emailNotVerified': 'Email not verified',
  'auth.refusal.sessionExpired': 'Session expired. Re-authenticate to perform this action.',
  'auth.refusal.lastAccount': "You can't unlink your last account",
  'auth.refusal.socialLinked': 'Social account already linked',
  'auth.refusal.linkedExists': 'Linked account already exists',
  'auth.refusal.passwordSet': 'User already has a password set',
  /** Our own refusal of an account nobody invited (src/lib/auth.ts). */
  'auth.refusal.signupClosed': 'signup closed',
  /** The auth endpoints' rate limit answered. */
  'auth.refusal.rateLimited': 'Too many requests. Please try again later.',
} as const;
